import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, Boxes } from 'lucide-react';
import type { FinancialIndicator } from '../../types/dashboard';

interface IndicatorCardProps {
  indicator: FinancialIndicator;
  showEmptyState?: boolean;
}

const IndicatorCard: React.FC<IndicatorCardProps> = ({ indicator, showEmptyState }) => {
  const formatValue = (value: number): string => {
    if (indicator.currency) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const getTrendColor = (trend: number) => {
    if (trend > 0) return 'text-emerald-600';
    if (trend < 0) return 'text-red-600';
    return 'text-blue-600';
  };

  const getBackgroundGradient = (trend: number) => {
    if (trend > 0) return 'bg-gradient-to-br from-emerald-50 to-white';
    if (trend < 0) return 'bg-gradient-to-br from-red-50 to-white';
    return 'bg-gradient-to-br from-blue-50 to-white';
  };

  if (showEmptyState) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-sm font-medium text-gray-500 mb-2">{indicator.label}</h3>
        <div className="text-center py-4">
          <Boxes className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nenhum registro ainda</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl shadow-lg border border-gray-100 overflow-hidden ${getBackgroundGradient(indicator.trend)}`}>
      <div className="px-6 py-5">
        <div className="flex items-start justify-between">
          <h3 className="text-sm font-medium text-gray-500">{indicator.label}</h3>
          {indicator.trend !== 0 && (
            <span className={`flex items-center text-sm font-semibold ${getTrendColor(indicator.trend)}`}>
              {indicator.trend > 0 ? (
                <ArrowUpIcon className="h-4 w-4 mr-1" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 mr-1" />
              )}
              {Math.abs(indicator.trend)}%
            </span>
          )}
        </div>
        
        <div className="mt-4">
          <span className={`text-2xl font-bold ${getTrendColor(indicator.trend)}`}>
            {formatValue(indicator.value)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default IndicatorCard;